import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import api from "components/api/api";
import toast from "react-hot-toast";
import "./GroupTargetSelector.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const initials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

// ─── Component ────────────────────────────────────────────────────────────────

const GroupTargetSelector = ({ selectedGroupIds, onChange, onUsersChange }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const [activeGroupId, setActiveGroupId] = useState(null);

  // Per-group staged user sets: { [groupId]: Set<userId> }
  const [groupUserSets, setGroupUserSets] = useState({});

  const [userSearch, setUserSearch] = useState("");
  // FIX: separate search for right panel (group members)
  const [memberSearch, setMemberSearch] = useState("");

  // Show selected users bar
  const [showSelected, setShowSelected] = useState(false);

  // Create-group modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDesc, setNewGroupDesc] = useState("");
  const [creating, setCreating] = useState(false);

  // Edit group modal (rename + delete)
  const [showEditModal, setShowEditModal] = useState(false);
  const [editGroupId, setEditGroupId] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [editGroupDesc, setEditGroupDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Drag state
  const dragItem = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  // Resizable panel height
  const [panelHeight, setPanelHeight] = useState(420);
  const resizeStart = useRef(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await api.fetchUsers();
      // const users = (res.data?.data || []).filter((u) => u.role !== "ADMIN");
      const users = res.data?.data || [];
      setAllUsers(users);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchGroups = useCallback(async (keepActiveId) => {
    setLoadingGroups(true);
    try {
      const res = await api.fetchGroups();
      const fetched = res.data || [];
      setGroups(fetched);

      setGroupUserSets((prev) => {
        const next = { ...prev };
        fetched.forEach((g) => {
          const serverIds = new Set(
            (g.users || []).map((u) =>
              typeof u === "object" ? u.userId || u.id : u,
            ),
          );
          const existing = prev[g.id];
          if (!existing || existing.size <= serverIds.size) {
            next[g.id] = serverIds;
          }
        });
        return next;
      });

      setActiveGroupId((curr) => {
        const target = keepActiveId ?? curr;
        if (target && fetched.find((g) => g.id === target)) return target;
        return fetched.length > 0 ? fetched[0].id : null;
      });
    } catch {
      toast.error("Failed to load groups");
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchGroups();
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────

  const activeGroup = groups.find((g) => g.id === activeGroupId) || null;
  const activeSet = groupUserSets[activeGroupId] || new Set();

  const filteredUsers = allUsers.filter((u) => {
    const q = userSearch.toLowerCase();
    if (!q) return true;
    return (
      (u.username || "").toLowerCase().includes(q) ||
      (u.staffId || "").toLowerCase().includes(q)
    );
  });

  const leftUsers = filteredUsers.filter((u) => !activeSet.has(u.id));

  // FIX: right panel also filterable via memberSearch
  const rightUsers = allUsers.filter((u) => {
    if (!activeSet.has(u.id)) return false;
    const q = memberSearch.toLowerCase();
    if (!q) return true;
    return (
      (u.username || "").toLowerCase().includes(q) ||
      (u.staffId || "").toLowerCase().includes(q)
    );
  });

  // All unique users across ALL selected groups (for the "Show Selected" bar)
  const allTargetedUserIds = new Set(
    groups
      .filter((g) => selectedGroupIds.includes(g.id))
      .flatMap((g) => [...(groupUserSets[g.id] || new Set())]),
  );
  const allTargetedUsers = allUsers.filter((u) => allTargetedUserIds.has(u.id));

  const targetedUserIdsArray = useMemo(
    () => [...allTargetedUserIds],
    [groups, selectedGroupIds, groupUserSets],
  );

  useEffect(() => {
    if (onUsersChange) {
      onUsersChange(targetedUserIdsArray);
    }
  }, [targetedUserIdsArray]);

  // ── Group selection toggle ─────────────────────────────────────────────────

  const handleToggleGroupSelected = (groupId) => {
    onChange(
      selectedGroupIds.includes(groupId)
        ? selectedGroupIds.filter((id) => id !== groupId)
        : [...selectedGroupIds, groupId],
    );
  };

  // ── Move users ─────────────────────────────────────────────────────────────

  const moveToRight = (userId) => {
    if (!activeGroupId) return;
    setGroupUserSets((prev) => {
      const next = new Set(prev[activeGroupId] || []);
      next.add(userId);
      return { ...prev, [activeGroupId]: next };
    });
  };

  const moveToLeft = (userId) => {
    if (!activeGroupId) return;
    setGroupUserSets((prev) => {
      const next = new Set(prev[activeGroupId] || []);
      next.delete(userId);
      return { ...prev, [activeGroupId]: next };
    });
  };

  const moveAllToRight = () => {
    if (!activeGroupId) return;
    setGroupUserSets((prev) => {
      const next = new Set(prev[activeGroupId] || []);
      leftUsers.forEach((u) => next.add(u.id));
      return { ...prev, [activeGroupId]: next };
    });
  };

  const moveAllToLeft = () => {
    if (!activeGroupId) return;
    setGroupUserSets((prev) => {
      const next = new Set(prev[activeGroupId] || []);
      rightUsers.forEach((u) => next.delete(u.id));
      return { ...prev, [activeGroupId]: next };
    });
  };

  // ── Save group membership ──────────────────────────────────────────────────

  const handleSaveGroup = async () => {
    if (!activeGroupId) return;
    const userIds = [...(groupUserSets[activeGroupId] || [])];
    try {
      await api.updateGroup(activeGroupId, { userIds });
      toast.success("Group saved");
    } catch {
      toast.error("Failed to save group");
    }
  };

  // ── Create group ───────────────────────────────────────────────────────────

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error("Group name is required");
      return;
    }
    setCreating(true);
    try {
      const res = await api.createGroup({
        name: newGroupName.trim(),
        description: newGroupDesc.trim(),
        userIds: [],
      });
      const created = res.data;
      toast.success("Group created");
      setShowCreateModal(false);
      setNewGroupName("");
      setNewGroupDesc("");
      await fetchGroups(created?.id);
      if (created?.id) {
        onChange([...selectedGroupIds, created.id]);
      }
    } catch {
      toast.error("Failed to create group");
    } finally {
      setCreating(false);
    }
  };

  // ── Open edit modal ────────────────────────────────────────────────────────

  const openEditModal = (e, group) => {
    e.stopPropagation();
    setEditGroupId(group.id);
    setEditGroupName(group.name || "");
    setEditGroupDesc(group.description || "");
    setShowEditModal(true);
  };

  // ── Save rename ────────────────────────────────────────────────────────────

  const handleSaveEdit = async () => {
    if (!editGroupName.trim()) {
      toast.error("Group name is required");
      return;
    }
    setSaving(true);
    try {
      await api.updateGroup(editGroupId, {
        name: editGroupName.trim(),
        description: editGroupDesc.trim(),
      });
      toast.success("Group renamed");
      setShowEditModal(false);
      await fetchGroups(editGroupId);
    } catch {
      toast.error("Failed to rename group");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete group (from edit modal) ─────────────────────────────────────────

  const handleDeleteFromModal = async () => {
    if (!window.confirm(`Delete "${editGroupName}"? This cannot be undone.`))
      return;
    setDeleting(true);
    try {
      await api.deleteGroup(editGroupId);
      toast.success("Group deleted");
      onChange(selectedGroupIds.filter((id) => id !== editGroupId));
      setGroupUserSets((prev) => {
        const next = { ...prev };
        delete next[editGroupId];
        return next;
      });
      setShowEditModal(false);
      if (activeGroupId === editGroupId) setActiveGroupId(null);
      await fetchGroups();
    } catch {
      toast.error("Failed to delete group");
    } finally {
      setDeleting(false);
    }
  };

  // ── Drag & Drop ────────────────────────────────────────────────────────────

  const handleDragStart = (e, userId, source) => {
    dragItem.current = { userId, source };
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, target) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOver(target);
  };

  const handleDrop = (e, target) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragItem.current) return;
    const { userId, source } = dragItem.current;
    if (source === "left" && target === "right") moveToRight(userId);
    if (source === "right" && target === "left") moveToLeft(userId);
    dragItem.current = null;
  };

  const handleDragLeave = () => setDragOver(null);
  const handleDragEnd = () => {
    dragItem.current = null;
    setDragOver(null);
  };

  // ── Resize handle (right-side grip dots) ──────────────────────────────────

  const handleResizeMouseDown = (e) => {
    resizeStart.current = { y: e.clientY, h: panelHeight };
    document.addEventListener("mousemove", handleResizeMouseMove);
    document.addEventListener("mouseup", handleResizeMouseUp);
    e.preventDefault();
  };

  const handleResizeMouseMove = (e) => {
    if (!resizeStart.current) return;
    const delta = e.clientY - resizeStart.current.y;
    setPanelHeight(Math.max(280, Math.min(800, resizeStart.current.h + delta)));
  };

  const handleResizeMouseUp = () => {
    resizeStart.current = null;
    document.removeEventListener("mousemove", handleResizeMouseMove);
    document.removeEventListener("mouseup", handleResizeMouseUp);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="gts-root">
      {/* ── Group strip ── */}
      <div className="gts-group-strip">
        <div className="gts-groups-scroll">
          {loadingGroups ? (
            <span className="gts-strip-empty">Loading groups…</span>
          ) : groups.length === 0 ? (
            <span className="gts-strip-empty">No groups yet.</span>
          ) : (
            groups.map((g) => {
              const isActive = g.id === activeGroupId;
              const isChecked = selectedGroupIds.includes(g.id);
              const memberCount =
                (groupUserSets[g.id] || new Set()).size || g.users?.length || 0;
              return (
                <div
                  key={g.id}
                  className={`gts-group-chip ${isActive ? "gts-group-chip--active" : ""} ${isChecked ? "gts-group-chip--checked" : ""}`}
                  onClick={() => setActiveGroupId(g.id)}
                >
                  <input
                    type="checkbox"
                    className="gts-chip-check"
                    checked={isChecked}
                    onChange={() => {}}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleGroupSelected(g.id);
                    }}
                    title="Include in quiz targeting"
                  />
                  <span className="gts-chip-name">
                    {g.name || `Group ${groups.indexOf(g) + 1}`}
                  </span>
                  <span className="gts-chip-count">{memberCount}</span>
                  {/* FIX: pencil icon instead of ti-dots */}
                  <button
                    className="gts-chip-edit"
                    onClick={(e) => openEditModal(e, g)}
                    title="Rename / delete group"
                  >
                    {" "}
                    ⋮
                  </button>
                </div>
              );
            })
          )}
        </div>
        <button
          className="gts-new-group-btn"
          onClick={() => setShowCreateModal(true)}
        >
          <i className="ti ti-plus" aria-hidden="true" /> New group
        </button>
      </div>

      {/* ── Summary bar ── */}
      <div className="gts-summary">
        {selectedGroupIds.length === 0 ? (
          <span className="gts-summary-hint">
            Check groups above to include them in targeting.
          </span>
        ) : (
          <span className="gts-summary-text">
            <strong>{selectedGroupIds.length}</strong> group
            {selectedGroupIds.length !== 1 ? "s" : ""} selected
            {" · "}
            <strong>{allTargetedUserIds.size}</strong> users targeted
          </span>
        )}
        <div className="gts-summary-actions">
          {allTargetedUsers.length > 0 && (
            <button
              className="gts-show-selected-btn"
              onClick={() => setShowSelected((v) => !v)}
            >
              {showSelected ? "Hide selected" : `Show selected`}
              <i
                className={`ti ${showSelected ? "ti-chevron-up" : "ti-chevron-down"}`}
                aria-hidden="true"
              />
            </button>
          )}
          {activeGroup && (
            <button className="gts-save-btn" onClick={handleSaveGroup}>
              <i className="ti ti-device-floppy" aria-hidden="true" /> Save "
              {activeGroup.name}"
            </button>
          )}
        </div>
      </div>

      {/* ── Show Selected bar ── */}
      {showSelected && allTargetedUsers.length > 0 && (
        <div className="gts-selected-bar">
          <span className="gts-selected-bar-label">
            <strong>{allTargetedUsers.length} staff selected</strong>
            <button className="gts-clear-all-btn" onClick={() => onChange([])}>
              Clear all
            </button>
          </span>
          <div className="gts-selected-tags">
            {allTargetedUsers.map((u) => (
              <span key={u.id} className="gts-selected-tag">
                {u.username}
                <button
                  className="gts-tag-remove"
                  // FIX: Only deselect groups that contain this user — do NOT
                  // mutate groupUserSets. This merely un-checks the group from
                  // the quiz targeting (selectedGroupIds), it does NOT remove
                  // the user from the group's member list on the right panel.
                  onClick={() => {
                    const groupsContainingUser = selectedGroupIds.filter(
                      (gid) => groupUserSets[gid]?.has(u.id),
                    );
                    // Remove those groups from the selected list
                    onChange(
                      selectedGroupIds.filter(
                        (gid) => !groupsContainingUser.includes(gid),
                      ),
                    );
                  }}
                  title={`Remove ${u.username} from targeting (deselects their group)`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Dual-panel editor ── */}
      {activeGroupId ? (
        <>
          <div className="gts-panels" style={{ height: panelHeight }}>
            {/* ── LEFT: all users ── */}
            <div
              className={`gts-panel ${dragOver === "left" ? "gts-panel--dragover" : ""}`}
              onDragOver={(e) => handleDragOver(e, "left")}
              onDrop={(e) => handleDrop(e, "left")}
              onDragLeave={handleDragLeave}
            >
              <div className="gts-panel-header">
                <span className="gts-panel-title">All users</span>
                <span className="gts-panel-count">{leftUsers.length}</span>
              </div>

              <div className="gts-panel-search">
                <i
                  className="ti ti-search gts-search-icon"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder="Search by name or staff ID…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="gts-search-input"
                />
                {userSearch && (
                  <button
                    className="gts-search-clear"
                    onClick={() => setUserSearch("")}
                  >
                    <i className="ti ti-x" aria-hidden="true" />
                  </button>
                )}
              </div>

              <div className="gts-panel-actions">
                <button
                  className="gts-action-btn"
                  onClick={moveAllToRight}
                  disabled={leftUsers.length === 0}
                  title="Add all to group"
                >
                  Add all{" "}
                  <i className="ti ti-chevrons-right" aria-hidden="true" />
                </button>
              </div>

              <div className="gts-user-list">
                {loadingUsers ? (
                  <div className="gts-list-empty">Loading users…</div>
                ) : leftUsers.length === 0 ? (
                  <div className="gts-list-empty">
                    {userSearch
                      ? "No users match your search."
                      : "All users added to this group."}
                  </div>
                ) : (
                  leftUsers.map((u) => (
                    <div
                      key={u.id}
                      className="gts-user-row"
                      draggable
                      onDragStart={(e) => handleDragStart(e, u.id, "left")}
                      onDragEnd={handleDragEnd}
                      onDoubleClick={() => moveToRight(u.id)}
                      title="Drag to group, or double-click to add"
                    >
                      <span className="gts-drag-handle" aria-hidden="true">
                        <i className="ti ti-grip-vertical" />
                      </span>
                      <div className="gts-avatar">{initials(u.username)}</div>
                      <div className="gts-user-info">
                        <span className="gts-user-name">{u.username}</span>
                        {u.staffId && (
                          <span className="gts-user-meta">{u.staffId}</span>
                        )}
                        {u.outlet && (
                          <span className="gts-user-meta">{u.outlet}</span>
                        )}
                      </div>
                      <button
                        className="gts-move-btn"
                        onClick={() => moveToRight(u.id)}
                        title="Add to group"
                      >
                        <i className="ti ti-chevron-right" aria-hidden="true" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ── CENTER space── */}
            <div className="gts-center-arrows"></div>

            {/* ── RIGHT: group members ── */}
            <div
              className={`gts-panel ${dragOver === "right" ? "gts-panel--dragover" : ""}`}
              onDragOver={(e) => handleDragOver(e, "right")}
              onDrop={(e) => handleDrop(e, "right")}
              onDragLeave={handleDragLeave}
            >
              <div className="gts-panel-header">
                <span className="gts-panel-title">
                  {activeGroup?.name || "Group"} members
                </span>
                {/* Show total member count (unfiltered) */}
                <span className="gts-panel-count">
                  {allUsers.filter((u) => activeSet.has(u.id)).length}
                </span>
              </div>

              {/* FIX: Search bar for right panel (group members) */}
              <div className="gts-panel-search">
                <i
                  className="ti ti-search gts-search-icon"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder="Search members…"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="gts-search-input"
                />
                {memberSearch && (
                  <button
                    className="gts-search-clear"
                    onClick={() => setMemberSearch("")}
                  >
                    <i className="ti ti-x" aria-hidden="true" />
                  </button>
                )}
              </div>

              <div className="gts-panel-actions">
                <button
                  className="gts-action-btn gts-action-btn--danger"
                  onClick={moveAllToLeft}
                  disabled={rightUsers.length === 0}
                  title="Remove all from group"
                >
                  <i className="ti ti-chevrons-left" aria-hidden="true" />{" "}
                  Remove all
                </button>
              </div>

              <div className="gts-user-list">
                {rightUsers.length === 0 ? (
                  <div className="gts-list-empty gts-list-empty--drop">
                    <i
                      className="ti ti-users"
                      style={{ fontSize: 24 }}
                      aria-hidden="true"
                    />
                    <span>
                      {memberSearch
                        ? "No members match your search."
                        : "Drag users here or use arrows"}
                    </span>
                  </div>
                ) : (
                  rightUsers.map((u) => (
                    <div
                      key={u.id}
                      className="gts-user-row gts-user-row--member"
                      draggable
                      onDragStart={(e) => handleDragStart(e, u.id, "right")}
                      onDragEnd={handleDragEnd}
                      onDoubleClick={() => moveToLeft(u.id)}
                      title="Drag to remove, or double-click to remove"
                    >
                      <span className="gts-drag-handle" aria-hidden="true">
                        <i className="ti ti-grip-vertical" />
                      </span>
                      <div className="gts-avatar gts-avatar--member">
                        {initials(u.username)}
                      </div>
                      <div className="gts-user-info">
                        <span className="gts-user-name">{u.username}</span>
                        {u.staffId && (
                          <span className="gts-user-meta">{u.staffId}</span>
                        )}
                        {u.outlet && (
                          <span className="gts-user-meta">{u.outlet}</span>
                        )}
                      </div>
                      <button
                        className="gts-move-btn gts-move-btn--remove"
                        onClick={() => moveToLeft(u.id)}
                        title="Remove from group"
                      >
                        <i className="ti ti-chevron-left" aria-hidden="true" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* ── Resize handle — grip dots on the RIGHT side ── */}
          <div className="gts-resize-handle-wrapper">
            <div
              className="gts-resize-handle"
              onMouseDown={handleResizeMouseDown}
              title="Drag to resize"
            >
              <span className="gts-resize-dots" aria-hidden="true">
                <i className="ti ti-grip-horizontal" />⠿
              </span>
            </div>
          </div>
        </>
      ) : (
        <div className="gts-no-group">
          <i
            className="ti ti-users-group"
            style={{ fontSize: 32 }}
            aria-hidden="true"
          />
          <p>
            Select a group above to manage its members, or create a new one.
          </p>
        </div>
      )}

      {/* ── Create Group Modal ── */}
      {showCreateModal && (
        <div
          className="gts-modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div className="gts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gts-modal-header">
              <span>Create new group</span>
              <button
                className="gts-modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                <i className="ti ti-x" aria-hidden="true" />
              </button>
            </div>
            <label className="gts-modal-label">
              Group name{" "}
              <span style={{ color: "var(--color-text-danger, #dc3545)" }}>
                *
              </span>
            </label>
            <input
              type="text"
              className="gts-modal-input"
              placeholder="e.g. Oman Mall Staff"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateGroup()}
              autoFocus
            />
            <label className="gts-modal-label">Description</label>
            <input
              type="text"
              className="gts-modal-input"
              placeholder="Optional"
              value={newGroupDesc}
              onChange={(e) => setNewGroupDesc(e.target.value)}
            />
            <div className="gts-modal-actions">
              <button
                className="gts-modal-cancel"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button
                className="gts-modal-confirm"
                onClick={handleCreateGroup}
                disabled={creating}
              >
                {creating ? "Creating…" : "Create group"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Group Modal (rename + delete) ── */}
      {showEditModal && (
        <div
          className="gts-modal-overlay"
          onClick={() => setShowEditModal(false)}
        >
          <div className="gts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gts-modal-header">
              <span>Edit group</span>
              <button
                className="gts-modal-close"
                onClick={() => setShowEditModal(false)}
              >
                <i className="ti ti-x" aria-hidden="true" />
              </button>
            </div>
            <label className="gts-modal-label">
              Group name{" "}
              <span style={{ color: "var(--color-text-danger, #dc3545)" }}>
                *
              </span>
            </label>
            <input
              type="text"
              className="gts-modal-input"
              value={editGroupName}
              onChange={(e) => setEditGroupName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSaveEdit()}
              autoFocus
            />
            <label className="gts-modal-label">Description</label>
            <input
              type="text"
              className="gts-modal-input"
              placeholder="Optional"
              value={editGroupDesc}
              onChange={(e) => setEditGroupDesc(e.target.value)}
            />
            <div className="gts-modal-actions gts-modal-actions--spread">
              <button
                className="gts-modal-delete"
                onClick={handleDeleteFromModal}
                disabled={deleting}
                title="Delete this group permanently"
              >
                {deleting ? (
                  "Deleting…"
                ) : (
                  <>
                    <i className="ti ti-trash" aria-hidden="true" /> Delete
                    group
                  </>
                )}
              </button>
              <div className="gts-modal-actions-right">
                <button
                  className="gts-modal-cancel"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="gts-modal-confirm"
                  onClick={handleSaveEdit}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupTargetSelector;
